import orderStyles from "@crm/styles/order.module.css";
import {housekeeprApi} from "@crm/api/housekeepr-api";
import {skipToken} from "@reduxjs/toolkit/query";
import {useAppDispatch, useAppSelector} from "@crm/services/store";
import {
    selectWindowCleaningAddress,
    selectWindowCleaningFrequencyId,
    selectWindowCleaningPriceExpanded,
    selectWindowCleaningResidenceType,
    selectWindowCleaningSecondaryGlazing,
    selectWindowCleaningServiceId,
    selectWindowCleaningWindows
} from "@crm/services/window-cleaning-selectors";
import {WindowCleaningServicePriceRequest} from "@crm/api/models/requests/housekeepr-api";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronUp} from "@fortawesome/free-solid-svg-icons";
import {windowCleaningActions} from "@crm/services/window-cleaning-slice";
import {frequencyDisplayString} from "@crm/api/models/common/housekeepr-api";
import useMediaQuery from "@crm/hooks/use-media-query/use-media-query";
import {useTheme} from "styled-components";
import React from "react";

export const WindowCleaningPrice = () => {
    const dispatch = useAppDispatch();

    const selectedAddress = useAppSelector(selectWindowCleaningAddress);
    const selectedServiceId = useAppSelector(selectWindowCleaningServiceId);
    const selectedFrequencyId = useAppSelector(selectWindowCleaningFrequencyId);
    const selectedResidenceType = useAppSelector(selectWindowCleaningResidenceType);
    const selectedWindowTypes = useAppSelector(selectWindowCleaningWindows);
    const hasSecondaryGlazing = useAppSelector(selectWindowCleaningSecondaryGlazing);
    const verifiedCode = useAppSelector(state => state.windowCleaning.verifiedCode);
    const isExpanded = useAppSelector(selectWindowCleaningPriceExpanded);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('mobileM'));

    const {data: addressResult} = housekeeprApi.useRegisterAddressQuery(
        selectedAddress ? {darId: selectedAddress.adresse.id} : skipToken
    );

    const {data: serviceResult} = housekeeprApi.useGetServiceQuery(selectedServiceId ?? skipToken);

    const frequency = serviceResult?.frequencies.find(f => f.id == selectedFrequencyId);

    const {data: windowTypesResult} = housekeeprApi.useGetWindowTypesQuery(selectedServiceId ?? skipToken);

    const useCode = !!verifiedCode;

    const {data: codeResult} = housekeeprApi.useCheckCodeQuery(
        useCode && selectedServiceId && selectedFrequencyId ? {
            code: verifiedCode,
            service_id: selectedServiceId,
            frequency_id: selectedFrequencyId,
        } : skipToken
    );

    const canSearchPrice = !!selectedServiceId && !!selectedResidenceType && selectedWindowTypes.filter(window => window.count > 0).length > 0 && !!addressResult;

    const priceRequest = canSearchPrice ? {
        service_id: selectedServiceId,
        address_id: addressResult.id,
        frequency_id: selectedFrequencyId,
        has_secondary_glazing: hasSecondaryGlazing,
        residence_type: selectedResidenceType,
        windows: selectedWindowTypes,
    } as WindowCleaningServicePriceRequest : undefined;

    const {
        data: priceResult,
        isFetching: isFetchingPrice,
    } = housekeeprApi.useGetServicePriceQuery(priceRequest ?? skipToken);

    const priceWithoutVAT = priceResult ?? 0;
    const priceWithVAT = priceWithoutVAT * 1.25;
    const discount = useCode ? priceWithVAT * ((codeResult?.discount_code?.percent_off ?? 0) / 100) + (codeResult?.discount_code?.value ?? 0) : 0;
    const priceWithVATAndDiscount = priceWithVAT - discount;
    const frequencyDiscount = priceWithVATAndDiscount * ((frequency?.discount ?? 0) / 100);
    const priceWithVATAndDiscounts = priceWithVATAndDiscount - frequencyDiscount;
    const deduction = Math.round(priceWithVATAndDiscounts * (serviceResult?.craftsman_deduction ?? 26) / 100);
    const finalPrice = priceWithVATAndDiscounts - deduction;

    if (!canSearchPrice) {
        return <></>;
    }

    return <>
        <div className={`${orderStyles.priceDetails} ${isExpanded ? orderStyles.expanded : ''}`}>
            <h4 className={orderStyles.header}>Prisoversigt</h4>
            {windowTypesResult && selectedWindowTypes.length > 0 && selectedWindowTypes?.map(windowType => {
                const type = windowTypesResult.find(w => w.id == windowType.id);
                return <div key={windowType.id} className={orderStyles.priceRow}>
                    <div>{type?.name ? `${type.name}` : `Vindue ${windowType.id}`}</div>
                    <div>{windowType.count} stk.</div>
                </div>;
            })}
            <div className={orderStyles.priceDivider}/>
            <div className={orderStyles.priceRow}>
                <div>Pris pr. besøg</div>
                <div>{priceWithVAT.toLocaleString('da-DK', {
                    currency: 'DKK',
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                })} kr
                </div>
            </div>
            {discount > 0 &&
                <div className={orderStyles.priceRow}>
                    <div>Rabatkode</div>
                    <div>{(-discount).toLocaleString('da-DK', {
                        currency: 'DKK',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    })} kr
                    </div>
                </div>
            }
            {frequency && frequencyDiscount > 0 &&
                <div className={orderStyles.priceRow}>
                    <div>{frequencyDisplayString(frequency)}</div>
                    <div>{(-frequencyDiscount).toLocaleString('da-DK', {
                        currency: 'DKK',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    })} kr
                    </div>
                </div>
            }
            <div className={orderStyles.priceRow}>
                <div>Total</div>
                <div style={{textDecoration: 'underline'}}>
                    {(priceWithVATAndDiscounts).toLocaleString('da-DK', {
                        currency: 'DKK',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    })} kr
                </div>
            </div>
            <div className={orderStyles.priceRow} style={{opacity: 0.5}}>
                <div>Efter servicefradrag (ca. {serviceResult?.craftsman_deduction ?? 26}%)</div>
                <div>
                    {(finalPrice).toLocaleString('da-DK', {
                        currency: 'DKK',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    })} kr
                </div>
            </div>
        </div>
        <div className={`${orderStyles.priceFooter}`}
             onClick={() => dispatch(windowCleaningActions.togglePriceExpanded())}>
            <h4 className={orderStyles.header} style={{alignSelf: 'flex-start'}}>
                Din pris efter fradrag og rabat
            </h4>
            <div className={orderStyles.footerContent}>
                <div>
                    {priceResult && !isFetchingPrice ? `${finalPrice.toLocaleString('da-DK', {
                        currency: 'DKK',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    })} kr.` : (
                        <div style={{display: 'flex'}}>
                            <div className={orderStyles.spinnerContainer}>
                                <div className={orderStyles.spinner}></div>
                            </div>
                            <p className="ml-2">Indlæser pris...</p>
                        </div>
                    )}
                </div>
                <div
                    className={`${orderStyles.button} ${orderStyles.flexRow} ${orderStyles.flexMainEnd}`}
                    style={{gap: '8px'}}
                >
                    {isExpanded ? 'Luk' : 'Se mere'}
                    <FontAwesomeIcon
                        icon={isExpanded ? faChevronDown : faChevronUp}
                        size="xs"
                        fixedWidth={true}
                        style={{width: 20, height: 20}}
                    />
                </div>
            </div>
        </div>
    </>;
}

export default WindowCleaningPrice;